import url from "../constants/urlList";
import { CONSTANTS } from "../constants/app-constant";
import { claimEntryHeader } from "../utils/entry-header";
import { reportPendingPracticeEmailClick } from "../utils/growth-receipt";
import { isApiFailure } from "../sagas/apiFailure";
const errMsg = 'Something went wrong';

const doGetCall = (apiUrl, options) => {
    // structuredErrorBody is a doGetCall option, not a fetch option: only the
    // strict daily-question parser opts in to receiving the parsed non-200 body
    // spread onto the result (it reads a structured 410 tombstone). It must
    // never be the default — an error body carrying payload-like keys (data,
    // items, count, …) would read as SUCCESS to any of the other callers that
    // truthy-check a field instead of status, which is the exact
    // misclassification class the #587 gate closed. Everyone else keeps the
    // established { status, errMsg } shape.
    const { structuredErrorBody = false, ...fetchOptions } = options ?? {};
    const request = options === undefined ? fetch(apiUrl) : fetch(apiUrl, fetchOptions);
    return request
        .then(async (response) => {
            if (response.status === 200) {
                return response.json();
            }

            const responseBody = await response?.json().catch(() => errMsg);
            if (!structuredErrorBody) {
                return { status: response.status, errMsg: responseBody };
            }
            const structuredBody = responseBody !== null
                && typeof responseBody === "object"
                && !Array.isArray(responseBody)
                ? responseBody
                : {};
            return {
                ...structuredBody,
                status: response.status,
                // Preserve the established error shape while also exposing the
                // structured tombstone to the opted-in strict parser. status is
                // written after the spread so a body's own status key never wins.
                errMsg: responseBody
            };
        })
        .catch(() => ({
            // A network reject must resolve to the same {status, errMsg} shape as
            // any other failure: leaking the raw Error let every consumer's
            // errStatus/status check misclassify an offline call as SUCCESS
            // (false empty/blank states — the #587 gate finding, fixed as a class).
            status: 500,
            errMsg
        }));
};

/**
 * The one body-carrying request path. POST/PUT/PATCH differ ONLY in the verb, so they share an
 * implementation rather than three copies of the same response contract — the copies were the
 * thing that drifted (a PATCH helper added for QIR B7 duplicated this block verbatim).
 */
const doBodyCall = (method, apiUrl, data) => {
    const requestOptions = {
        method,
        headers: { "Content-Type": "application/json" }
    };

    if (data !== undefined) {
        requestOptions.body = JSON.stringify(data);
    }

    return fetch(apiUrl, requestOptions)
        .then(async (response) => {
            if (response.status === 200 || response.status === 201) {
                return response.json();
            }

            return {
                errStatus: response.status,
                // A non-2xx may carry no JSON body (flag-gated empty 404, proxy HTML).
                // Guard the parse so it can't reject into the outer catch and get
                // mislabeled a network "500" — that would hide a dark 404 (feature
                // off) as a server error. Mirrors doGetCall's contract.
                errMsg: await response?.json().catch(() => errMsg)
            };
        })
        .catch(() => {
            return {
                errStatus: "500",
                errMsg: CONSTANTS.errorMsgs[500],
            };
        });
};

const doUpdateCall = (apiUrl, data, isPutCall = false) =>
    doBodyCall(isPutCall ? "PUT" : "POST", apiUrl, data);

/**
 * PATCH. Same response contract as the other body-carrying verbs, because it is the same code
 * path. Added for QIR B7: the reference-asset METADATA edit endpoint is a @PatchMapping, and
 * sending it as a POST would hit no mapping and surface as a 405 the console would special-case.
 */
const doPatchCall = (apiUrl, data) => doBodyCall("PATCH", apiUrl, data);

const doDeleteCall = (apiUrl) => {
    const requestOptions = {
        method: "DELETE",
        headers: { "Content-Type": "application/json" }
    };

    return fetch(apiUrl, requestOptions)
        .then(async (response) => {
            if (response.status === 200) {
                return response.json();
            }

            return {
                errStatus: response.status,
                // A non-2xx may carry no JSON body (flag-gated empty 404, proxy HTML).
                // Guard the parse so it can't reject into the outer catch and get
                // mislabeled a network "500" — that would hide a dark 404 (feature
                // off) as a server error. Mirrors doGetCall's contract.
                errMsg: await response?.json().catch(() => errMsg)
            };
        })
        .catch(() => {
            return {
                errStatus: "500",
                errMsg: CONSTANTS.errorMsgs[500],
            };
        });
};

// QLink same-case student guidance (QLINK-EXPLANATION-CONTRACT-SEPT16): the
// non-200 resolve carries the response status TWICE by design. Top-level
// `status` is the saga's failure signal (isApiFailure — 401/404/400/500 are
// failures, never an empty success), and errMsg is a message-LESS object the
// metadata saga's `errMsg?.message || errMsg` extraction forwards INTACT into
// the reducer's questionMetadataErrMsg — which is how the drawer keeps the
// response status for its sign-in/unavailable/retry split without a new
// action/reducer/saga layer.
//
// Round-2 provenance: the caller's receipt (unique request id + non-secret
// auth snapshot, built by exam.jsx — NEVER a raw token) is echoed onto every
// outcome so the exam page can verify a response answers its LATEST request
// under the CURRENT auth before adopting it. It never touches the wire.
// The page's `platform` label when it passes the QLink label rule, else null.
const qlinkOpenPlatform = () => {
    try {
        const value = new URLSearchParams(window.location.search).get('platform');
        const normalized = value ? value.trim().toLowerCase() : '';
        return /^[a-z0-9_-]{1,50}$/.test(normalized) ? normalized : null;
    } catch {
        // No readable location (non-browser runtime): send no label, as before.
        return null;
    }
};

const qlinkGuidanceFailure = (status, receipt) => ({
    status,
    errMsg: { qlinkGuidance: true, status, ...(receipt ? { receipt } : {}) },
});

const NOTES_TO_QUIZ_LINK_STATES = new Set(["READY", "CLAIMED", "UNAVAILABLE"]);

// The notes-to-quiz open ping. A newer backend answers 200 with a small link
// context; an older one, and the rate limiter, answer 204 with no body. Only a
// 200 JSON object in a known state counts as a context — anything else is
// null, which the claim page reads as "behave as before". Never rejects, and
// sets no Authorization header: the store.js fetch interceptor owns that.
const doOpenPingCall = (apiUrl) => {
    const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" }
    };

    return fetch(apiUrl, requestOptions)
        .then(async (response) => {
            let context = null;
            if (response.status === 200) {
                try {
                    const body = await response.json();
                    if (body !== null
                        && typeof body === "object"
                        && NOTES_TO_QUIZ_LINK_STATES.has(body.state)) {
                        context = body;
                    }
                } catch {
                    // Not JSON: the ping still counts, there is just no context.
                }
            }
            return {
                ok: response.ok,
                status: response.status,
                context
            };
        })
        .catch(() => ({
            ok: false,
            status: 500,
            context: null
        }));
};

export const api = {
    getDailyQuestion(examType = 'ABSITE', signedOutSessionQuestionId) {
        return doGetCall(`${url.dailyQuestion}/${encodeURIComponent(examType)}`, {
            structuredErrorBody: true,
            headers: {
                "X-QLearn-Session-Question-Id": signedOutSessionQuestionId,
            },
        });
    },
    getImmutableDailyQuestion(
        examType,
        date,
        revisionId,
        revisionHash,
        signedOutSessionQuestionId
    ) {
        const identityPath = [examType, date, revisionId, revisionHash]
            .map((segment) => encodeURIComponent(segment))
            .join("/");
        return doGetCall(`${url.dailyQuestion}/${identityPath}`, {
            structuredErrorBody: true,
            headers: {
                "X-QLearn-Session-Question-Id": signedOutSessionQuestionId,
            },
        });
    },
    getDailyQuestionByTouch(touchId, signedOutSessionQuestionId) {
        return doGetCall(
            `${url.dailyQuestion}/touch/${encodeURIComponent(touchId)}`,
            {
                structuredErrorBody: true,
                headers: {
                    "X-QLearn-Session-Question-Id": signedOutSessionQuestionId,
                },
            }
        );
    },
    submitDailyQuestionAnswer(examType, answerRequest) {
        return doUpdateCall(
            `${url.dailyQuestion}/${encodeURIComponent(examType)}/answer`,
            answerRequest
        );
    },
    issueDailyQuestionUnlockGrant(examType, grantRequest) {
        return doUpdateCall(
            `${url.dailyQuestion}/${encodeURIComponent(examType)}/unlock-grant`,
            grantRequest
        );
    },
    getDailyQuestionExplanation(examType, date, revisionId, revisionHash, grantId) {
        const query = new URLSearchParams({ date, revisionId, revisionHash });
        const headers = /^dqg_[0-9a-f]{64}$/.test(grantId || "")
            ? { "X-QLearn-Daily-Question-Grant": grantId }
            : {};
        return doGetCall(
            `${url.dailyQuestion}/${encodeURIComponent(examType)}/explanation?${query.toString()}`,
            { structuredErrorBody: true, headers }
        );
    },
    addUserApi(user) {
        return doUpdateCall(url.addUser, user);
    },
    doLogin(user) {
        return doUpdateCall(url.login, user);
    },
    // Native Sign in with Apple (Step-1 native-auth infra). Backend verifies the
    // identityToken against Apple's JWKS and completes the shared session path.
    appleSignIn(appleAuthData) {
        return doUpdateCall(url.appleAuth, appleAuthData);
    },
    getUserDetails(userId) {
        return doGetCall(url.userDetails + userId);
    },
    getUserDetailsFromToken() {
        // The account's first lookup carries its first-touch entry, once (utils/entry-header).
        const entry = claimEntryHeader();
        const lookup = entry
            ? doGetCall(url.userDetailsFromToken, { headers: entry.headers }).then((result) => {
                if (isApiFailure(result)) {
                    entry.release(); // the server never logged it: the next lookup carries it
                }
                return result;
            })
            : doGetCall(url.userDetailsFromToken);
        return lookup.then((result) => {
            if (!isApiFailure(result)) {
                // Signed in: a pending landing from the practice email is reported, once and
                // fire-and-forget (utils/growth-receipt); the lookup's result is unchanged.
                void reportPendingPracticeEmailClick(); // never rejects; not awaited on purpose
            }
            return result;
        });
    },
    createQuiz(quizData) {
        return doUpdateCall(url.createQuiz, quizData);
    },
    startAbsiteDiagnostic(diagnosticData) {
        return doUpdateCall(url.startAbsiteDiagnostic, diagnosticData);
    },
    requestAbsiteProgramCode(requestData) {
        return doUpdateCall(url.absiteProgramCodeRequest, requestData);
    },
    getAbsiteDiagnosticReport(quizId) {
        return doGetCall(`${url.diagnostic}/${quizId}/report`);
    },
    getAbsiteProgramSummary(filters = {}) {
        const queryParams = new URLSearchParams();
        if (filters.programCode) {
            queryParams.append('programCode', filters.programCode);
        }
        if (filters.from) {
            queryParams.append('from', filters.from);
        }
        if (filters.to) {
            queryParams.append('to', filters.to);
        }

        const query = queryParams.toString();
        return doGetCall(`${url.absiteProgramSummary}${query ? `?${query}` : ''}`);
    },
    // --- Generic ReviewQueue (new /qlearn/review/* backend; flag-gated dark) ---
    // Reviewer worklist: PENDING items of one type. Grouped client-side by
    // subjectKey (=questionId) into per-question candidate sets.
    getReviewQueue(filters = {}) {
        const queryParams = new URLSearchParams();
        queryParams.append('type', filters.type || 'IMAGE_CANDIDATE');
        if (filters.examType) queryParams.append('examType', filters.examType);
        if (filters.unreviewedByMe !== undefined) queryParams.append('unreviewedByMe', filters.unreviewedByMe);
        // status=PENDING (default, omitted) | DECIDED | ALL. The status rail's
        // DECIDED fetch; an old backend ignores the param and returns PENDING —
        // callers guard via filterDecidedItems (mixed-deploy safe both ways).
        if (filters.status) queryParams.append('status', filters.status);
        if (filters.page !== undefined) queryParams.append('page', filters.page);
        if (filters.size !== undefined) queryParams.append('size', filters.size);
        return doGetCall(`${url.reviewQueue}/queue?${queryParams.toString()}`);
    },
    // Admin queue-status snapshot: two-axis counts + reviewer activity for a type.
    getReviewQueueStatus(filters = {}) {
        const queryParams = new URLSearchParams();
        queryParams.append('type', filters.type || 'IMAGE_CANDIDATE');
        if (filters.examType) queryParams.append('examType', filters.examType);
        return doGetCall(`${url.reviewQueue}/queue/status?${queryParams.toString()}`);
    },
    // Reviewer FLAG upsert. Body: { decision, isBest, comment } (image type).
    // isBest is exclusive per question server-side (picking one clears siblings).
    voteReviewItem(itemId, body) {
        return doUpdateCall(`${url.reviewQueue}/item/${itemId}/vote`, body);
    },
    // #584 triple-review (rq-triple-review-contract §5), reconciled to QLearn-service#579.
    // All-candidates read for one question → QuestionCandidatesDto: the FULL candidate
    // set (decided included, AD-5) as ReviewItemDto[] + each item's reviewStatus + the
    // caller's saved myVote + assignment { assignedToMe, canRate, assignedReviewerIds
    // (admin-only) }, provenance/model redacted for non-admins (AD-4). The queue LIST is
    // QuestionCandidatesPage { questions: QuestionCandidatesDto[], totalElements, ... }.
    // 404 while the flag is dark. Shapes handled by review-queue-logic.adaptQuestionResponse
    // / flattenQuestionsResponse.
    getReviewQuestion(subjectKey) {
        return doGetCall(`${url.reviewQueue}/questions/${encodeURIComponent(subjectKey)}`);
    },
    // QIR B2 (§7.2): review-scoped question CONTEXT (stem/options/answer key/all
    // explanation fields) → ReviewQuestionContextDto. This is the F3 cure: the review
    // screen reads its base-question context HERE instead of the student-gated bank
    // egress (POST /bank/question), which strips explanations for every role while
    // strip-unanswered is on. Fail-closed server ACL: a non-admin caller must hold an
    // ACTIVE assignment on the subject; unauthorized/unknown → 404 (the doGetCall
    // contract surfaces { status: 404 } — QuestionDetail renders the assigned-reviewers
    // tile, no error banner). groundingStatus is admin-only (absent for tutors).
    getReviewQuestionContext(subjectKey, type = 'IMAGE_CANDIDATE') {
        const queryParams = new URLSearchParams();
        queryParams.append('type', type);
        return doGetCall(
            `${url.reviewQueue}/question-context/${encodeURIComponent(subjectKey)}?${queryParams.toString()}`
        );
    },
    // All-candidates queue LIST (AD-5): GET /qlearn/review/questions?type&examType&page&size
    // → QuestionCandidatesPage { questions: QuestionCandidatesDto[] (each with the FULL
    // active candidate set — decided included — + group assignment), totalElements, ... }.
    // This is the #584 P1 grid source: unlike /queue (PENDING-only worklist), it returns
    // every question's complete candidate set so a mixed (pending+decided) question renders
    // ALL its candidates in one N-up grid. 404 while the flag is dark. Shapes normalized by
    // review-queue-logic.flattenQuestionsResponse.
    getReviewQuestionsPage(filters = {}) {
        const queryParams = new URLSearchParams();
        queryParams.append('type', filters.type || 'IMAGE_CANDIDATE');
        if (filters.examType) queryParams.append('examType', filters.examType);
        if (filters.page !== undefined) queryParams.append('page', filters.page);
        if (filters.size !== undefined) queryParams.append('size', filters.size);
        return doGetCall(`${url.reviewQueue}/questions?${queryParams.toString()}`);
    },
    // ONE comparative decision per question group (#584 A2, T-CMP U1): PICK_BEST or
    // REJECT_SET, derived server-side into the unchanged per-candidate rows. Assigned
    // tutor or admin (server-enforced); 409 when the group has no PENDING candidate.
    decideReviewQuestion(subjectKey, body, type = 'IMAGE_CANDIDATE') {
        const queryParams = new URLSearchParams();
        queryParams.append('type', type);
        return doUpdateCall(
            `${url.reviewQueue}/questions/${encodeURIComponent(subjectKey)}/decision?${queryParams.toString()}`,
            body
        );
    },
    // Admin-only reviewer assignment (AD-2), keyed subjectKey + reviewType. The ACL is
    // server-enforced (@PreAuthorize ROLE_ADMIN on every endpoint); a non-admin caller
    // gets a non-2xx which the doGetCall/doUpdateCall/doDeleteCall contract surfaces as
    // { status|errStatus }, so the admin-only UI simply stays hidden.
    // List active assignees for a question → ReviewAssignmentDto[] (assignedReviewerId etc.).
    getReviewAssignments(subjectKey, type = 'IMAGE_CANDIDATE') {
        const queryParams = new URLSearchParams();
        queryParams.append('subjectKey', subjectKey);
        queryParams.append('type', type);
        return doGetCall(`${url.reviewQueue}/assignments?${queryParams.toString()}`);
    },
    // Assign a reviewer. Body: ReviewAssignmentRequest { subjectKey, reviewType, reviewerId }.
    // Idempotent server-side (re-activates an existing row); returns ReviewAssignmentDto.
    assignReviewer(subjectKey, reviewerId, reviewType = 'IMAGE_CANDIDATE') {
        return doUpdateCall(`${url.reviewQueue}/assignments`, { subjectKey, reviewType, reviewerId });
    },
    // Revoke a reviewer (soft-delete; idempotent no-op if none active). The endpoint takes
    // subjectKey/type/reviewerId as QUERY params (DELETE has no body). Returns { revoked: true }.
    revokeReviewer(subjectKey, reviewerId, type = 'IMAGE_CANDIDATE') {
        const queryParams = new URLSearchParams();
        queryParams.append('subjectKey', subjectKey);
        queryParams.append('type', type);
        queryParams.append('reviewerId', reviewerId);
        return doDeleteCall(`${url.reviewQueue}/assignments?${queryParams.toString()}`);
    },
    // QIR B5 (§4): admin bulk assign by criteria. `preview=true` resolves the blast radius
    // and returns { matchedCount, perReviewer } with ZERO writes (I1) — the console MUST
    // preview before enabling Confirm. On commit returns { matchedCount, created,
    // alreadyAssigned, perReviewer, batchLabel }. Over-cap → 400 (surfaced as errStatus).
    // Body: { criteria, reviewerIds, strategy: 'ALL_TO_EACH'|'ROUND_ROBIN', cap?, batchLabel? }.
    bulkAssignReviewers(body, { preview = false } = {}) {
        const queryParams = new URLSearchParams();
        queryParams.append('preview', String(Boolean(preview)));
        return doUpdateCall(`${url.reviewQueue}/assignments/bulk?${queryParams.toString()}`, body);
    },
    // QIR B5 (I4): bulk revoke every active assignment carrying `batchLabel` (soft, audited).
    // Returns { revoked: n }.
    bulkRevokeByBatch(batchLabel) {
        const queryParams = new URLSearchParams();
        queryParams.append('batchLabel', batchLabel);
        return doDeleteCall(`${url.reviewQueue}/assignments/bulk?${queryParams.toString()}`);
    },
    // --- QIR B7 (§3.2): grounding CURATION. These four server endpoints have shipped for a
    // while and were wired into NO client, which is why prod has zero reference assets and the
    // grounding panel has never had content to show. The bytes proxy (getReferenceAssetImage,
    // below) was already wired — it is deliberately NOT duplicated here.
    // All ADMIN-only server-side; a non-admin caller gets a non-2xx surfaced as { errStatus },
    // so the admin-only console simply stays hidden. Rights rules (PD→citation required,
    // CC→citation+license required, unknown corpus→400) are enforced by the SERVER; the form
    // mirrors them for UX and must surface the server's named 400 verbatim when they disagree.
    createReferenceAsset(body) {
        return doUpdateCall(`${url.reviewQueue}/reference-assets`, body);
    },
    // Metadata only — caption/citation/license/corpus/sourceRef. Bytes are immutable by design
    // (new bytes = new asset), so there is no byte field to send here.
    editReferenceAsset(assetId, body) {
        return doPatchCall(
            `${url.reviewQueue}/reference-assets/${encodeURIComponent(assetId)}`, body);
    },
    // Union-merge one asset onto the question's grounding row (audited ADMIN_ATTACH). The
    // ACTIVE-link cap is server-enforced with a named 400 — never silently truncated, so the
    // console surfaces that message rather than swallowing it.
    attachGroundingLink(subjectKey, assetId, type = 'IMAGE_CANDIDATE') {
        const queryParams = new URLSearchParams();
        queryParams.append('type', type);
        return doUpdateCall(
            `${url.reviewQueue}/question-grounding/${encodeURIComponent(subjectKey)}/links?${queryParams.toString()}`,
            { assetId });
    },
    // Tombstone (never delete) one link. The server REQUIRES a non-blank reason.
    unlinkGroundingLink(subjectKey, assetId, reason, type = 'IMAGE_CANDIDATE') {
        const queryParams = new URLSearchParams();
        queryParams.append('type', type);
        if (reason !== undefined) queryParams.append('reason', reason);
        return doDeleteCall(
            `${url.reviewQueue}/question-grounding/${encodeURIComponent(subjectKey)}/links/${encodeURIComponent(assetId)}?${queryParams.toString()}`);
    },
    // QIR B7 VERIFICATION-class pointer: { sourceType: 'TEXTBOOK_DB'|'WEB_REFERENCE', ref?,
    // citation, snippetText?, url? }. Citation-only by design — there is no bytes field, because
    // third-party material is CITED, not copied, while owner decision D1 is unruled.
    addGroundingPointer(subjectKey, body, type = 'IMAGE_CANDIDATE') {
        const queryParams = new URLSearchParams();
        queryParams.append('type', type);
        return doUpdateCall(
            `${url.reviewQueue}/question-grounding/${encodeURIComponent(subjectKey)}/pointers?${queryParams.toString()}`,
            body);
    },
    // Tombstone one pointer; reason REQUIRED server-side (blank → named 400).
    removeGroundingPointer(subjectKey, pointerId, reason, type = 'IMAGE_CANDIDATE') {
        const queryParams = new URLSearchParams();
        queryParams.append('type', type);
        if (reason !== undefined) queryParams.append('reason', reason);
        return doDeleteCall(
            `${url.reviewQueue}/question-grounding/${encodeURIComponent(subjectKey)}/pointers/${encodeURIComponent(pointerId)}?${queryParams.toString()}`);
    },
    // QIR B6: admin textbook-page suggestions for a question → { state, suggestions[], model,
    // corpusPageCount, corpusPageCountAnyModel, skippedDimensionMismatch }.
    // `state` is load-bearing and must NOT be flattened to "empty": NOT_CONFIGURED (bridge off),
    // NO_VECTORS (this question has none), CORPUS_MODEL_MISMATCH (the corpus holds vectors but
    // not for the configured model — a config fault affecting every question), and OK are four
    // different facts, and the rail says which. 404 while B6 is dark.
    getTextbookSuggestions(subjectKey, { type = 'IMAGE_CANDIDATE', limit } = {}) {
        const queryParams = new URLSearchParams();
        queryParams.append('type', type);
        if (limit !== undefined) queryParams.append('limit', String(limit));
        return doGetCall(
            `${url.reviewQueue}/grounding/suggestions/${encodeURIComponent(subjectKey)}?${queryParams.toString()}`);
    },
    // Admin-only reset (AD-3): reopens all items in the question to PENDING, supersedes
    // (never deletes) prior votes, writes a ReviewResetAudit. Body { confirm, reason }.
    // AD-6: 409 when a candidate is PUBLISHED/RETRACTED (reset invalid) — surfaced
    // non-destructively caller-side. doUpdateCall resolves { errStatus } on non-2xx.
    resetReviewQuestion(subjectKey, body) {
        return doUpdateCall(`${url.reviewQueue}/questions/${encodeURIComponent(subjectKey)}/reset`, body);
    },
    // Phase-2 reasoning-clarify round on this reviewer's vote. `answer` undefined
    // triggers the sufficiency check + first follow-up; a string answers the open
    // follow-up. 404 while the clarify flag is dark (handled gracefully caller-side).
    clarifyReviewItem(itemId, answer) {
        return doUpdateCall(`${url.reviewQueue}/item/${itemId}/clarify`,
            answer === undefined ? {} : { answer });
    },
    // Phase-2 per-question suitability toggle. Body:
    // { questionId, examType, unsuitableForImage?, unsuitableForTable?, imageReason?, tableReason? }.
    // A null/omitted flag leaves that axis unchanged server-side. 404 while the
    // suitability flag is dark (handled gracefully caller-side).
    markSuitability(body) {
        return doUpdateCall(`${url.reviewQueue}/suitability`, body);
    },
    // Suitability read for the dashboard tile (+ producers). 404 while dark.
    getSuitability(examType) {
        const query = examType ? `?examType=${encodeURIComponent(examType)}` : '';
        return doGetCall(`${url.reviewQueue}/suitability${query}`);
    },
    // Phase-3.3 read-only per-question drill-down: every ReviewQueue signal for one
    // question (items + reasoning + clarify threads + suitability + corrections). 404 dark.
    getQuestionDrilldown(questionId) {
        return doGetCall(`${url.reviewQueue}/question/${encodeURIComponent(questionId)}/drilldown`);
    },
    // Read-only operator analytics (throughput/latency/backlog/outcomes/clarify). 404 dark.
    getReviewAnalytics({ type = "IMAGE_CANDIDATE", examType = "ABSITE", windowDays = 0 } = {}) {
        const q = new URLSearchParams({ type, examType, windowDays });
        return doGetCall(`${url.reviewQueue}/analytics?${q.toString()}`);
    },
    // --- Admin publish console (all three @Secured(ROLE_ADMIN) server-side; 404 dark) ---
    // Publish worklist: APPROVED items not yet published.
    getPublishPending(filters = {}) {
        const queryParams = new URLSearchParams();
        queryParams.append('type', filters.type || 'IMAGE_CANDIDATE');
        if (filters.examType) queryParams.append('examType', filters.examType);
        if (filters.page !== undefined) queryParams.append('page', filters.page);
        if (filters.size !== undefined) queryParams.append('size', filters.size);
        return doGetCall(`${url.reviewQueue}/publish/pending?${queryParams.toString()}`);
    },
    // Batch publish (the explicit two-stage promotion step — side-car CDN copy,
    // never a bank write). Returns { publishBatchId, published, skipped[{id,reason}] }.
    // S1.2/F-S5-1: optional extras carry the PER-ITEM stemLeakageAttestedItemIds and
    // overrideItemIds (+ required overrideReason) — never batch-level booleans
    // (the server 400s the retired overrideDangerFlagged boolean by design).
    publishReviewItems(itemIds, extras) {
        return doUpdateCall(`${url.reviewQueue}/publish`, { itemIds, ...(extras || {}) });
    },
    // Emergency rollback of one publish batch. Returns { retracted: [ids] }.
    retractPublishBatch(batchId) {
        return doUpdateCall(`${url.reviewQueue}/publish/${encodeURIComponent(batchId)}/retract`, undefined);
    },
    // Correction console: the read-only QBank apply export (staged mutations + diffs). 404 dark.
    getBankMutationExport(examType = "ABSITE") {
        return doGetCall(`${url.reviewQueue}/bank-mutations/export?examType=${encodeURIComponent(examType)}`);
    },
    // Content-quality analytics (read-only). 404 while dark. Flagged worst-first list +
    // per-question composed health (difficulty + discrimination + anomaly flags).
    getContentQualityFlagged(limit = 50) {
        return doGetCall(`${url.contentQuality}/questions/flagged?limit=${encodeURIComponent(limit)}`);
    },
    getContentQualityHealth(questionId) {
        return doGetCall(`${url.contentQuality}/question/${encodeURIComponent(questionId)}/health`);
    },
    getContentQualityDistractors(questionId) {
        return doGetCall(`${url.contentQuality}/question/${encodeURIComponent(questionId)}/distractors`);
    },
    getContentQualityCategories(dimension = "organSystem", limit = 50) {
        return doGetCall(`${url.contentQuality}/categories?dimension=${encodeURIComponent(dimension)}&limit=${encodeURIComponent(limit)}`);
    },
    getContentQualityTrend(questionId) {
        return doGetCall(`${url.contentQuality}/question/${encodeURIComponent(questionId)}/trend`);
    },
    // SME CSV download (read-only). The global fetch is auth-patched, so a plain <a href>
    // could not carry the Bearer token — fetch the bytes here and return a Blob (or a
    // { status } marker on non-200 so the caller can render the dark/error state).
    getContentQualityDistractorCsv(limit = 50) {
        return fetch(`${url.contentQuality}/distractors/export?limit=${encodeURIComponent(limit)}`)
            .then((res) => (res.ok ? res.blob() : { status: res.status }))
            .catch((err) => err);
    },
    // NOTE: no applyBankMutation client method by design — the APPLY boundary is
    // owner-gated (Q5 ROLE_PUBLISHER, Akshay-gated) and the backend endpoint always
    // 403s. The console UI's Apply control is permanently disabled; do NOT add a
    // client caller here.
    // Role-gated proxy for an un-published candidate image → object URL (the
    // patched global fetch attaches the Bearer token; a plain <img src> could not).
    getReviewItemImage(itemId) {
        return fetch(`${url.reviewQueue}/item/${itemId}/image`)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`image request failed: ${response.status}`);
                }
                return response.blob();
            })
            .then((blob) => URL.createObjectURL(blob));
    },
    // S3-U1 / R-P1-1: role-gated proxy for a grounding reference asset image → object URL.
    // Dedicated endpoint (readImage is hard-bound to IMAGE_CANDIDATE items); same Bearer-gated
    // fetch-then-object-URL pattern as getReviewItemImage. @PreAuthorize TUTOR||ADMIN, dark-404.
    getReferenceAssetImage(assetId) {
        return fetch(`${url.reviewQueue}/reference-assets/${encodeURIComponent(assetId)}/image`)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`reference image request failed: ${response.status}`);
                }
                return response.blob();
            })
            .then((blob) => URL.createObjectURL(blob));
    },
    claimNotesToQuiz(token) {
        return doUpdateCall(`${url.notesToQuiz}/${encodeURIComponent(token)}/claim`, undefined);
    },
    // Persist the session-fiducial id-book (full cumulative
    // {eventId, recordingId, qToTag} from SessionIdBook.toJSON()) so the
    // Zoom-recording decoder can map painted tag indices back to question-ids.
    // PUT = idempotent upsert keyed by eventId.
    upsertSessionFiducialIdBook(idBookPayload) {
        return doUpdateCall(url.sessionFiducialIdBook, idBookPayload, true);
    },
    trackNotesToQuizOpen(token) {
        return doOpenPingCall(`${url.notesToQuiz}/${encodeURIComponent(token)}/opened`);
    },
    saveAbsiteDiagnosticProfile(profileData) {
        return doUpdateCall(
            `${url.diagnostic}/${profileData.quizId}/profile`,
            profileData.profile,
            true
        );
    },
    submitQuiz(quizData) {
        const quizUrl = `${url.submitQuiz}/${quizData.quizHistory.quizId}/user/${quizData.quizHistory.userId}/submit`;

        if (quizData?.quizEndMode === "ADHOC") {
            const adhocPayload = { quizEndMode: quizData.quizEndMode, quizHistory: { quizSubmitDate: quizData?.quizHistory?.quizSubmitDate } };
            return doUpdateCall(quizUrl, adhocPayload);
        } 
        return doUpdateCall(quizUrl, quizData);
    },
    // Server-authoritative per-question grading (in-quiz egress Slice B;
    // QLearn-service #483). The caller's Bearer token rides the patched global
    // fetch, and the server enforces owner + tutorOn + IN_PROGRESS before
    // grading. Returns { correct, question } where question is the REVEALED
    // view (answer key + explanations) — the answer is final server-side, so a
    // re-POST returns the first recorded result and never re-grades.
    // Runtime client config (in-quiz egress C0; QLearn-service #489). The
    // endpoint is authenticated: an unauthenticated fetch returns a non-200
    // shape and the caller no-ops — fail-open to flag-OFF (today's path).
    getClientConfig() {
        return doGetCall(url.clientConfig);
    },
    submitQuestionAnswer(quizId, questionId, selectedAnswer) {
        return doUpdateCall(
            `${url.submitQuiz}/${quizId}/question/${encodeURIComponent(questionId)}/answer`,
            { selectedAnswer }
        );
    },
    // Guided Learning (GL-1 §1/§3) client callers, per the server-owned wire
    // contract (docs/gl-api-wire-contract.md, QLearn-service). /gl/why and
    // /gl/assisted callers were REMOVED (ROW1 gate B4/B5 cure): WHY rides the
    // card-draft call at grade time, and the assisted signal of record is the
    // server-side hint-serve row. These return the standard {errStatus, errMsg}
    // non-200 shape; gl-service.js adapts it to the typed glError contract.
    requestGlHint({ questionId, hintIndex }) {
        // NEVER carries WHY (ruling C-1/C-9).
        return doUpdateCall(url.glHint, { questionId, hintIndex });
    },
    postGlEvent(record) {
        // `record` is already allowlist-sanitized by gl-events.buildGlEvent;
        // the server re-redacts and overrides the subject key regardless.
        return doUpdateCall(url.glEvent, record);
    },
    getCategories() {
        return doGetCall(url.categories);
    },
    getQuesCategories() {
        return doGetCall(url.questionCategories);
    },
    getQuestionHistory(quizId) {
        return doGetCall(url.questionHistory + quizId);
    },
    getQuiz(quizId) {
        return doGetCall(url.getQuiz + quizId);
    },
    getQuizHistory(quizId) {
        return doGetCall(url.quizHistory + quizId);
    },
    getStudentQuizHistory(quizId) {
        return doGetCall(url.studentQuizHistory + quizId);
    },
    getOverallQuizHistory(quizId) {
        return doGetCall(url.overallQuizHistory + quizId);
    },
    getOverallStudentQuiz(params) {
        const status = params.isCompleted ? "COMPLETE" : "IN_PROGRESS";
        return doGetCall(`${url.overallStudentQuiz}/${params.userId}?status=${status}`);
    },
    getQuestionMetadata(questionList) {
        // QLink same-case student guidance: ONLY an explicit qlinkStudent
        // context ({ linkId, token }) reroutes this fetch to the dedicated
        // authenticated route. The share token rides the JSON body — never
        // the URL and never a bearer; the patched global fetch (store.js)
        // attaches the real login Bearer. An unusable context is refused as
        // unavailable rather than silently widening back to the generic bank
        // route, and absent context keeps every other caller byte-identical.
        const { quizId, qlinkStudent, ...body } = questionList || {};
        if (qlinkStudent) {
            const { receipt } = qlinkStudent;
            const questionIds = [...(body.question_list || [])].filter(Boolean);
            if (!qlinkStudent.linkId || !qlinkStudent.token || !questionIds.length) {
                return Promise.resolve(qlinkGuidanceFailure(404, receipt));
            }
            return fetch(
                `${url.fetchQlink}/${encodeURIComponent(qlinkStudent.linkId)}/student-questions`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    // The wire body is exactly the backend contract — the
                    // receipt is UI-side provenance and never leaves the client.
                    body: JSON.stringify({ token: qlinkStudent.token, questionIds }),
                }
            )
                .then((response) => {
                    if (response.status !== 200) {
                        return qlinkGuidanceFailure(response.status, receipt);
                    }
                    return response.json().then((parsed) => {
                        if (receipt && parsed && typeof parsed === "object") {
                            // Non-index property: Array.isArray/length/iteration
                            // are untouched; the saga stores it verbatim.
                            parsed.qlinkReceipt = receipt;
                        }
                        return parsed;
                    });
                })
                .catch(() => qlinkGuidanceFailure(500, receipt));
        }
        const requestUrl = quizId
            ? `${url.questionMetadata}?quizId=${encodeURIComponent(quizId)}`
            : url.questionMetadata;
        const requestOptions = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        };
        return fetch(requestUrl, requestOptions)
            .then((response) => {
                if (response.status === 200) {
                    return response.json();
                }

                return {
                    status: response.status,
                    errMsg: errMsg
                };
            })
            .catch(() => ({
            // A network reject must resolve to the same {status, errMsg} shape as
            // any other failure: leaking the raw Error let every consumer's
            // errStatus/status check misclassify an offline call as SUCCESS
            // (false empty/blank states — the #587 gate finding, fixed as a class).
            status: 500,
            errMsg
        }));
    },
    // Admin-only author attribution for the review-drawer byline (the fields
    // were removed from the student-facing question payload in QLearn-service
    // #484; this endpoint is @PreAuthorize(ROLE_ADMIN)). Returns
    // {questionId,userId,userEmail}; a non-admin/anon caller gets a non-200
    // which doGetCall surfaces as {status,...} so the byline just stays hidden.
    getQuestionAuthor(questionId) {
        return doGetCall(`${url.questionMetadata}/${questionId}/author`);
    },
    reviewQuestion(param) {
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(param.reviewTime),
        };

        return fetch(
            `${url.reviewQuestion}/${param.questionId}/review`,
            requestOptions
        )
            .then((response) => {
                if (response.status === 200) {
                    return response.json();
                }

                return {
                    status: response.status,
                    errMsg: errMsg
                };
            })
            .catch(() => ({
            // A network reject must resolve to the same {status, errMsg} shape as
            // any other failure: leaking the raw Error let every consumer's
            // errStatus/status check misclassify an offline call as SUCCESS
            // (false empty/blank states — the #587 gate finding, fixed as a class).
            status: 500,
            errMsg
        }));
    },
    // getPackageList() {
    //     return doGetCall(url.packagePlan);
    // },
    getUserPackage(userid) {
        return doGetCall(`${url.userDetails}${userid}/plan/true`);
    },
    createPaymentSession(paymentData) {
        return doUpdateCall(url.paymentSession, paymentData);
    },
    updatePaymentStatus(param) {
        const statusUrl = param.isSuccess ? `${url.booking}/${param.bookingId}/status/SUCCESS` :
            `${url.booking}/${param.bookingId}/status/FAILED`;

        return doUpdateCall(statusUrl, undefined, true);
    },
    setUserActivity(paramData) {
        return doUpdateCall(url.userActivity + paramData.userId, paramData, true);
    },
    getUserActivity(userId) {
        return doGetCall(url.userActivity + userId);
    },
    getUserPayments(userId) {
        return doGetCall(url.userPayments + userId);
    },
    getInvitations() {
        return doGetCall(url.invitation + '?page=0&size=100');
    },
    cancelSubscription(bookingId) {
        return doUpdateCall(`${url.booking}/${bookingId}/${url.cancelSubscription}`, undefined);
    },
    // Server-side gate for the native (RevenueCat/StoreKit) purchase flow:
    // GET /qlearn/revenuecat/purchase-eligibility?productId=... returns
    // { purchaseAllowed, hasActiveAccess, mapped, reason? } for the current
    // user. Non-200s surface via doGetCall's { status, errMsg } shape, which
    // mobileBillingService treats as an eligibility-check failure (fail closed).
    getRevenueCatPurchaseEligibility(productId) {
        return doGetCall(`${url.revenueCatPurchaseEligibility}?productId=${encodeURIComponent(productId)}`);
    },
    sendInvitation(paramBody) {
        return doUpdateCall(url.paymentSession, paramBody);
    },
    resendInvitation(invitationId) {
        return doUpdateCall(`${url.invitation}/${invitationId}`, undefined, true);
    },
    getPreference(userId) {
        return doGetCall(`${url.preference}/${userId}`);
    },
    setPreference(param) {
        return doUpdateCall(`${url.preference}/${param.userId}`, param.body, true);
    },
    setFeedback(paramBody) {
        return doUpdateCall(url.paymentSession, paramBody);
    },
    setComment(param) {
        return doUpdateCall(`${url.comment}/${param.quesId}/comment`, param.commentParam);
    },
    getComment(param) {
        return doGetCall(`${url.comment}/${param.quesId}/comment`);
    },
    getImage(imageName) {
        return doGetCall(`${url.image}?imageName=${imageName}`);
    },
    getQuesFeedback(quesId) {
        return doGetCall(`${url.quesFeedback}/${quesId}`);
    },
    setQuesFeedback(paramBody) {
        return doUpdateCall(url.quesFeedback, paramBody);
    },
    getQuestionCount(paramBody) {
        return doUpdateCall(url.quesCatCount, paramBody);
    },
    getQuesTutorFeedback(quesId) {
        return doGetCall(`${url.quesTutorFeedback}/${quesId}`);
    },
    setQuesTutorFeedback(paramBody) {
        return doUpdateCall(url.quesTutorFeedback, paramBody);
    },
    getTutorFeedbackAll() {
        return doGetCall(url.tutorFeedbackAll);
    },
    getStudentFeedbackAll() {
        return doGetCall(url.studentFeedbackAll);
    },
    createTutorFeedbackBlock(block) {
        return doUpdateCall(url.feedbackBlock, block);
    },
    endTutorFeedbackBlock(blockId) {
        return doUpdateCall(`${url.feedbackBlock}/${blockId}/end`, undefined, true);
    },
    getAllPlans() {
        return doGetCall(url.packagePlan);
    },
    updatePlan(planData) {
        return doUpdateCall(`${url.packagePlan}/${planData.id}`, planData, true);
    },
    createPlan(planData) {
        return doUpdateCall(url.packagePlan, planData);
    },
    deletePlan(planId) {
        return doDeleteCall(`${url.packagePlan}/${planId}`);
    },
    selectPlan(body) {
        return doUpdateCall(`${url.userDetails}${body.userId}/plan/${body.planId}/select`, undefined, true);
    },
    getActivePlans(userId) {
        return doGetCall(`${url.userDetails}${userId}/plan?status=ACTIVE,CANCELLED,TRIALING`);
    },
    getTrialEligibility(userId) {
        return doGetCall(`${url.userDetails}${userId}/trial-eligible`);
    },
    sendInvites(invitesParam) {
        return doUpdateCall(url.sendInvites, invitesParam);
    },
    // Admin-gated user roster by role (server @PreAuthorize ROLE_ADMIN). Defaults to
    // STUDENT so the existing refund saga (calls with no arg) is unchanged; the review
    // assignment UI passes "TUTOR" to fetch the assignable tutor roster (displayName,
    // email, id). A non-admin caller gets a non-2xx surfaced as { status } by doGetCall.
    getUsersByRole(role = 'STUDENT') {
        return doGetCall(`${url.getUsersByRole}?role=${encodeURIComponent(role)}`);
    },
    getPaymentsByUser(userId) {
        return doGetCall(`${url.userPayments}${userId}?status=SUCCESS,CANCELED`);
    },
    refundBooking(bookingData) {
        const dataPayload = JSON.parse(JSON.stringify(bookingData));
        delete dataPayload.bookingId;
        return doUpdateCall(`${url.booking}/${bookingData.bookingId}/refund`, dataPayload);
    },
    getCategoryPreferences(userId) {
        return doGetCall(`${url.categoryPreference}${userId}`);
    },
    saveCategoryPreferences(data) {
        return doUpdateCall(`${url.categoryPreference}${data.userId}`, data.categoryMap);
    },
    searchTopics(query) {
        return doGetCall(`${url.topicsSearch}${query}`);
    },
    publishQlinkUrl(data) {
        return doUpdateCall(`${url.publishQlink}`, data.qlinkData);
    },
    revokeQlinkUrl(data) {
        return doUpdateCall(`${url.revokeQlink}${data?.qLinkID}`, undefined, true);
    },
    recordQLinkQuestionAnswered(data) {
        return doUpdateCall(url.qlinkQuestionAnswered, data);
    },
    fetchQlinkUrl(data) {
        return doGetCall(`${url.fetchQlink}${data?.query ? `?${data?.query}` : ''}`);
    },
    getDemoQuestionMetadata(questionList) {
        const token = window.localStorage.getItem('userAcquisitiontoken');

        const apiUrl = `${url.questionMetadata}?token=${token}`;
        const requestOptions = {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(questionList),
        };
        return fetch(apiUrl, requestOptions)
            .then((response) => {
                if (response.status === 200) {
                    return response.json();
                }

                return {
                    status: response.status,
                    errMsg: errMsg
                };
            })
            .catch(() => ({
            // A network reject must resolve to the same {status, errMsg} shape as
            // any other failure: leaking the raw Error let every consumer's
            // errStatus/status check misclassify an offline call as SUCCESS
            // (false empty/blank states — the #587 gate finding, fixed as a class).
            status: 500,
            errMsg
        }));
    },
    getDemoQuestionIDs(qlinkId) {
        const token = window.localStorage.getItem('userAcquisitiontoken');
        // No stored share token (stripped URL in a fresh browser): send none, so
        // the API answers 401 and the page can say "open the original link",
        // instead of sending the literal "null" (which used to 500).
        const params = new URLSearchParams();
        if (token) params.set('token', token);
        // Label the signed-out open with the page's campaign platform (same rule
        // as exam.jsx normalizeQLinkPlatform), so QUESTIONS_OPENED rows of
        // different paid arms can be told apart. The backend already accepts it.
        const platform = qlinkOpenPlatform();
        if (platform) params.set('platform', platform);
        const query = params.toString();
        const querySuffix = query ? '?' + query : '';
        const apiUrl = `${url.qlinkQues}/${qlinkId}${querySuffix}`;

        return fetch(apiUrl)
            .then(async (response) => {
                if (response.status === 200) {
                    return response.json();
                }

                return {
                    errStatus: response.status,
                    // Guard the parse so a bodyless non-2xx keeps its real status
                    // instead of rejecting into the outer catch as "500". Mirrors
                    // doGetCall / doUpdateCall.
                    errMsg: await response?.json().catch(() => errMsg)
                };
            })
            .catch(() => {
                return {
                    errStatus: "500",
                    errMsg: CONSTANTS.errorMsgs[500],
                };
            });
    },
    // Signed-out answer check for the one shared QLink question (first-experience
    // contract). The share token goes in the JSON BODY, never the URL. Resolves
    // {questionId, correct, correctAnswer} or {errStatus}; never rejects.
    checkQLinkAnswer({ qlinkId, questionId, answer, platform }) {
        const token = window.localStorage.getItem('userAcquisitiontoken');
        if (!token || !qlinkId || !questionId || !answer) {
            return Promise.resolve({ errStatus: 400 });
        }
        return fetch(`${url.qlinkQues}/${encodeURIComponent(qlinkId)}/answer-check`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token, questionId, answer, platform }),
        })
            .then(async (response) => {
                if (response.status !== 200) {
                    return { errStatus: response.status };
                }
                const data = await response.json().catch(() => null);
                return data && typeof data.correct === "boolean" && typeof data.correctAnswer === "string"
                    ? data
                    : { errStatus: 500 };
            })
            .catch(() => ({ errStatus: 500 }));
    },
    sendOTP(phoneData) {
        return doUpdateCall(url.sendOTP, phoneData);
    },
    verifyOTP(otpData) {
        return doUpdateCall(url.verifyOTP, otpData);
    },
    sendEmailOTP(emailData) {
        return doUpdateCall(url.sendEmailOTP, emailData);
    },
    verifyEmailOTP(emailOtpData) {
        return doUpdateCall(url.verifyEmailOTP, emailOtpData);
    },
    completeProfile(profileData) {
        return doUpdateCall(url.completeProfile, profileData);
    },
    getReferralCode() {
        return doGetCall(url.referralCode);
    },
    getReferralStats() {
        return doGetCall(url.referralStats);
    },
    // Semantic Power Search (flag-gated backend; empty-body 404 while disabled).
    // Uses a dedicated fetch (still authed via the global window.fetch override
    // in store.js) so a non-200 preserves the numeric status without calling
    // response.json() on an empty 404 body — which shared doUpdateCall does and
    // would turn the 404 into a generic rejection.
    powerSearch(payload) {
        return fetch(url.powerSearch, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        })
            .then((response) => {
                if (response.status === 200 || response.status === 201) {
                    return response.json();
                }
                return { errStatus: response.status };
            })
            // Truthy so the caller's error gate fires (a falsy 0 would read as "no error").
            .catch(() => ({ errStatus: -1 }));
    },
    // Flag-gated curriculum outline (QLearn-service curriculum slice 1): 404 while
    // dark. Dedicated fetch (as powerSearch above) so a 404 maps to {errStatus}
    // instead of a JSON-parse rejection on an empty body.
    getCurriculumOutline(edition) {
        return fetch(`${url.curriculumOutline}?edition=${encodeURIComponent(edition)}`)
            .then((response) => (response.status === 200 ? response.json() : { errStatus: response.status }))
            .catch(() => ({ errStatus: -1 }));
    },
    // Hydrate question stems for the returned ids. Backend DTO binds the body as
    // `question_list` (@JsonProperty), matching the existing exam flow.
    getQuestionsByIds(questionIds) {
        return doUpdateCall(url.questionMetadata, { question_list: questionIds });
    },
    // Flag-gated "More Like This": top-N active questions nearest a seed question by stored
    // BRIEF_EXPLANATION vector (QLearn-service PR #428). 404 when the backend flag is off.
    getMoreLikeThis(seedQuestionId) {
        return doGetCall(`${url.domain}/qlearn/quiz/question/${encodeURIComponent(seedQuestionId)}/more-like-this`);
    },
    // Tutor/admin signal on one served seed -> candidate recommendation. The server
    // upserts this reviewer pair; reviewer identity is always taken from auth.
    postMoreLikeThisFeedback(seedQuestionId, body) {
        return doUpdateCall(`${url.domain}/qlearn/quiz/question/${encodeURIComponent(seedQuestionId)}/more-like-this/feedback`, body);
    },
    // Flag-gated Audio Mode manifest (QLearn-service audio tranche 1). 404 when the
    // backend flag is off, the caller is outside the server audience gate, or the
    // question has no audio — all uniform by design.
    getAudioManifest(questionId) {
        return doGetCall(`${url.domain}/qlearn/quiz/question/${encodeURIComponent(questionId)}/audio-manifest`);
    },
    // Audio segment bytes → Blob (the getReviewItemImage precedent: the gated proxy
    // endpoint needs the Bearer token the patched global fetch attaches — a plain
    // <audio src> could not carry it). Manifest URLs are service-relative when
    // proxy-served and absolute when CDN-hosted; resolve both here.
    getAudioSegmentBlob(manifestUrl) {
        const absolute = /^https?:\/\//i.test(manifestUrl)
            ? manifestUrl
            : `${url.domain}/qlearn/${manifestUrl}`;
        return fetch(absolute)
            .then((res) => (res.ok ? res.blob() : { status: res.status }))
            .catch((err) => err);
    },
    // Tutor<->student mentorships (flag-gated; endpoints per
    // docs/qlearn-tutor-student-sharing-design.md). direction=incoming|outgoing,
    // status=PENDING|ACTIVE|... — both optional.
    listMentorships(params = {}) {
        const q = new URLSearchParams();
        if (params.direction) q.append("direction", params.direction);
        if (params.status) q.append("status", params.status);
        const qs = q.toString();
        return doGetCall(`${url.mentorships}${qs ? `?${qs}` : ""}`);
    },
    requestMentorship(body) {
        return doUpdateCall(url.mentorships, body);
    },
    // op = accept | decline | revoke (PUT /mentorships/{id}/{op}).
    respondToMentorship(mentorshipId, op) {
        return doUpdateCall(`${url.mentorships}/${mentorshipId}/${op}`, {}, true);
    },
    // Phase 2: a tutor reads a linked student's quiz history (server-side guarded
    // by an ACTIVE mentorship). Returns { studentName?, quizHistory:[...] } or a
    // quiz-history array; 404 until the backend guard/endpoint lands.
    getMentorshipStudentQuizHistory(studentUserId) {
        return doGetCall(`${url.mentorships}/students/${studentUserId}/quiz-history`);
    },
    // Tutor dashboard: post-session quiz emails the signed-in tutor was copied
    // on. No arguments on purpose — the server derives the tutor from the
    // session and accepts no tutor/student filter. 404 = feature dark.
    getTutorPostSessionQuizzes() {
        return doGetCall(`${url.mentorships}/post-session-quizzes`);
    },
    // Read-only receipt-scoped preview/results; never claims or submits a quiz.
    getTutorPostSessionQuizReview(rowKey) {
        return doGetCall(`${url.mentorships}/post-session-quizzes/${encodeURIComponent(rowKey)}/review`);
    },
    // "Do together" on a post-session quiz row (rows with canDoTogether only).
    // open: the server re-resolves the opaque rowKey among the caller's own rows,
    // makes sure the STUDENT's quiz exists and returns it with answer keys.
    // complete: saves { questionId: selectedAnswer } as the student's completed
    // quiz (409 once complete). Both 404 while
    // qlearn.mentorship.post-session-quizzes.do-together is dark.
    openTutorPostSessionQuiz(rowKey) {
        return doUpdateCall(`${url.mentorships}/post-session-quizzes/${encodeURIComponent(rowKey)}/open`);
    },
    completeQuizTogether(studentUserId, quizId, answers) {
        return doUpdateCall(
            `${url.mentorships}/students/${studentUserId}/quizzes/${quizId}/complete-together`,
            { answers }
        );
    },
    // Oversight quiz-open (contract qlearn-oversight-quiz-open-v1, QLS PR#606):
    // a linked tutor or admin opens a student's quiz READ-ONLY through the
    // guarded proxy routes. Server is the gate (403 unlinked, uniform 404 while
    // the qlearn.mentorship.oversight-quiz-open flag is dark). The questions
    // call reuses the student review screen's { question_list } payload shape;
    // the server returns always-gated views (answer key + explanations only for
    // questions the student answered — unanswered stay stripped, same as the
    // student's own review).
    getOversightQuiz(studentUserId, quizId) {
        return doGetCall(`${url.mentorships}/students/${studentUserId}/quizzes/${quizId}`);
    },
    getOversightQuizHistory(studentUserId, quizId) {
        return doGetCall(`${url.mentorships}/students/${studentUserId}/quizzes/${quizId}/quiz-history`);
    },
    getOversightQuestionHistory(studentUserId, quizId) {
        return doGetCall(`${url.mentorships}/students/${studentUserId}/quizzes/${quizId}/question-history`);
    },
    getOversightQuestionMetadata({ studentUserId, quizId, question_list }) {
        return doUpdateCall(
            `${url.mentorships}/students/${studentUserId}/quizzes/${quizId}/questions`,
            { question_list });
    },
    // Admin oversight: an Admin-Status caller lists EVERY student (server-paged +
    // optional search) to then open any one through getMentorshipStudentQuizHistory
    // above. The server guard (ROLE_ADMIN) is the gate — a non-admin gets 403.
    // Returns a Spring Page { content:[{studentUserId, displayName, email}],
    // totalElements, totalPages, number, size } or a {status} on 403/404.
    listAllStudentsAdmin(params = {}) {
        const q = new URLSearchParams();
        if (params.search) q.append("search", params.search);
        if (params.page != null) q.append("page", params.page);
        if (params.size != null) q.append("size", params.size);
        const qs = q.toString();
        return doGetCall(`${url.mentorships}/admin/students${qs ? `?${qs}` : ""}`);
    },
    // Phase 3: a tutor creates a quiz FOR a linked student (POST). Body:
    // { noOfQuestions, categoryMap, medicalCategoriesMap? }. The owner is the path
    // studentUserId; the server guards it with an ACTIVE mentorship granting
    // CREATE_QUIZ. 404s until the backend mentorship flag lands.
    assignMentorshipQuiz(studentUserId, body) {
        return doUpdateCall(`${url.mentorships}/students/${studentUserId}/quiz`, body);
    },
    // Unsaved tutor-led practice ("Do together"): a READ (POST only because the
    // filter is a body) of ≤ 20 questions for the tutor to lead on a shared
    // screen. Body: { noOfQuestions, categoryMap }. The server re-checks the
    // ACTIVE CREATE_QUIZ link on every call and creates NOTHING — no quiz, no
    // history. Resolves an array, or {errStatus, errMsg} (403 unlinked/revoked,
    // uniform 404 while qlearn.mentorship.guided-practice is dark).
    getGuidedPracticeQuestions(studentUserId, body) {
        return doUpdateCall(`${url.mentorships}/students/${studentUserId}/guided-practice/questions`, body);
    },
    // Separate coverage log, never a student answer or quiz submission.
    markGuidedPracticeCovered(studentUserId, questionId) {
        return doUpdateCall(`${url.mentorships}/students/${encodeURIComponent(studentUserId)}/guided-practice/questions/${encodeURIComponent(questionId)}/covered`, {}, true);
    },
    // Student→tutor question tags (design §10, slice T3; backend T1/T2 PRs
    // #478/#479). Both flags dark ⇒ every call 404s gracefully.
    // status=OPEN|ACKNOWLEDGED|RESOLVED, counterpartUserId — both optional.
    listQuestionTags(params = {}) {
        const q = new URLSearchParams();
        if (params.status) q.append("status", params.status);
        if (params.counterpartUserId) q.append("counterpartUserId", params.counterpartUserId);
        const qs = q.toString();
        return doGetCall(`${url.mentorshipTags}${qs ? `?${qs}` : ""}`);
    },
    // Body: { tutorUserId, questionId, note? } — the tagging student is ALWAYS
    // the JWT principal server-side (§10.3-C1); there is no student field to send.
    createQuestionTag(body) {
        return doUpdateCall(url.mentorshipTags, body);
    },
    // Student retraction (DELETE, soft-delete server-side; returns the DTO).
    retractQuestionTag(tagId) {
        return doDeleteCall(`${url.mentorshipTags}/${tagId}`);
    },
    // Tutor lifecycle (T4; §10.3-M1 row-resolved ownership server-side). PUT
    // with no body — the target tutor is read from the existing row by path id.
    // Every tutor by-id route answers ONE uniform 404 for missing/not-yours/
    // revoked (§10.3-N4) — callers must not distinguish.
    acknowledgeQuestionTag(tagId) {
        return doUpdateCall(`${url.mentorshipTags}/${tagId}/acknowledge`, undefined, true);
    },
    resolveQuestionTag(tagId) {
        return doUpdateCall(`${url.mentorshipTags}/${tagId}/resolve`, undefined, true);
    },
    // Tutor detail view (T4): tag + the student's attempts on the tagged
    // question ({tag, attempts:[{questionStem, answerGiven, correct, ...}]}).
    // VIEW_PROGRESS-gated server-side (§10.3-R3); same uniform 404 seam.
    getQuestionTagContext(tagId) {
        return doGetCall(`${url.mentorshipTags}/${tagId}/context`);
    }
};
